import { type ShowType } from '@/features/products/types';
import { useQuery } from '@tanstack/react-query';

import {
  getMaturityModel,
  getMaturityReportByGroup,
  getMaturityReportByOptions,
  getProductDraft,
  getProductMaturity,
  getProductMaturityModels,
  getProductRoadMaps,
  getProductsBasic,
  getProductsBrief,
  getProductsList,
  getProductsSimple,
} from '../api';
import { type ProductList } from '../types';

export const USE_MATURITY_MODEL_KEY = 'use-maturity-model-key';
export const USE_PRODUCT_ROADMAP_KEY = 'use-product-roadmap-key';
export const USE_PRODUCT_MATURITY_MODEL_KEY = 'use-product-maturity-model-key';
export const USE_PRODUCT_MATURITY_REPORT = 'use-product-maturity-report';

export const useProductRoadmap = (productId: number) =>
  useQuery({
    queryKey: [USE_PRODUCT_ROADMAP_KEY, productId],
    queryFn: async () => await getProductRoadMaps(productId),
    staleTime: 1000 * 60 * 10, // 10 minutes
  });

export const useProductsBasic = () =>
  useQuery({
    queryKey: ['products', 'basic'],
    queryFn: getProductsBasic,
    staleTime: 1000 * 60 * 15,
  });

export const useProductsBrief = () =>
  useQuery({
    queryKey: ['products', 'brief'],
    queryFn: getProductsBrief,
    staleTime: 1000 * 60 * 15,
  });

export function useProductsSimple(showType: ShowType[], industry?: string, productGroup?: string) {
  const showProducts = showType.includes('Show Solutions');
  const showAddons = showType.includes('Show Components');

  return useQuery({
    queryKey: ['products', 'simple', showProducts, showAddons, industry, productGroup],
    queryFn: async () => await getProductsSimple({ showProducts, showAddons, industry, productGroup }),
    staleTime: 1000 * 60 * 15,
  });
}

export const useProductMaturityModels = () =>
  useQuery({
    queryKey: [USE_PRODUCT_MATURITY_MODEL_KEY],
    queryFn: getProductMaturityModels,
    staleTime: 1000 * 60 * 5,
  });

export const useProductMaturityByMaturityGroup = (maturityId: number) =>
  useQuery({
    queryKey: [USE_PRODUCT_MATURITY_REPORT, 'group', maturityId],
    queryFn: async () => await getMaturityReportByGroup(maturityId),
    staleTime: 1000 * 60 * 15,
  });

export const useProductMaturity = (maturityModelId: number, enabled = true) =>
  useQuery({
    queryKey: [USE_PRODUCT_MATURITY_MODEL_KEY, maturityModelId],
    queryFn: async () => await getProductMaturity(maturityModelId),
    staleTime: 1000 * 60 * 15,
    enabled,
  });

export const useProductDraft = (productId: number, enabled = true) =>
  useQuery({
    queryKey: ['product-draft', productId],
    queryFn: async () => await getProductDraft(productId),
    staleTime: 1000 * 60 * 5,
    retry: false,
    enabled,
  });

export const useProductMaturityByOptions = (maturityId: number) =>
  useQuery({
    queryKey: [USE_PRODUCT_MATURITY_REPORT, 'options', maturityId],
    queryFn: async () => await getMaturityReportByOptions(maturityId),
    staleTime: 1000 * 60 * 15,
  });

export const useMaturityModel = (productId: number, maturityModelId: number | null, enabled = true) =>
  useQuery({
    queryKey: [USE_MATURITY_MODEL_KEY, productId, maturityModelId],
    queryFn: async () => await getMaturityModel(productId, maturityModelId ?? 0),
    staleTime: 1000 * 60 * 5,
    enabled: enabled && maturityModelId !== null,
  });

export const useProductsList = (initialData?: ProductList[]) =>
  useQuery({
    queryKey: ['products-list'],
    queryFn: getProductsList,
    staleTime: 1000 * 60 * 30,
    initialData,
  });
